import React, { useState, useEffect } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button,
  FormGroup, Label, Input
} from 'reactstrap';
import { supabase } from '../../supabaseClient';
import {
  FiYoutube, FiPlus, FiTrash2,
  FiLink
} from 'react-icons/fi';

const EMPTY = { youtube_url: '' };

const getYouTubeId = (url) => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : null;
};

const YoutubeManager = () => {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [selItem, setSelItem]     = useState(null);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const fetchRows = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('youtube')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const openAdd = () => {
    setForm(EMPTY); setError(''); setSuccess(''); setModal(true);
  };
  const openDel = (r) => { setSelItem(r); setDeleteModal(true); setError(''); setSuccess(''); };

  const validate = () => {
    if (!form.youtube_url.trim()) { setError('YouTube URL is required'); return false; }
    if (!getYouTubeId(form.youtube_url)) { setError('Please enter a valid YouTube URL'); return false; }
    setError('');
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (rows.length >= 6) {
      setError('Maximum of 6 videos allowed. Please delete some videos first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error } = await supabase.from('youtube').insert(form);
      if (error) throw error;
      setSuccess('Video added successfully!');
      setModal(false);
      fetchRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selItem) return;
    try {
      const { error } = await supabase.from('youtube').delete().eq('id', selItem.id);
      if (error) throw error;
      setSuccess('Video deleted successfully!');
      setDeleteModal(false);
      setSelItem(null);
      fetchRows();
    } catch (err) {
      setError(err.message);
    }
  };

  const previewId = getYouTubeId(form.youtube_url);

  return (
    <div style={s.wrap}>
      <div style={s.hdr}>
        <div>
          <h2 style={s.title}><FiYoutube style={{ marginRight: 10, color: '#ff0000' }} />YouTube Videos</h2>
          <p style={s.sub}>{rows.length} videos · Embedded portfolio</p>
        </div>
        <button style={s.addBtn} onClick={openAdd}>
          <FiPlus size={16} /> Add Video
        </button>
      </div>

      {success && (
        <div style={s.successBanner}>✓ {success}</div>
      )}
      {error && (
        <div style={s.errorBanner}>⚠ {error}</div>
      )}

      {loading ? (
        <div style={s.center}><span style={s.spinner} /></div>
      ) : rows.length === 0 ? (
        <div style={s.empty}>
          <FiYoutube size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>No videos yet. Add a YouTube URL to begin.</p>
        </div>
      ) : (
        <div style={s.grid}>
          {rows.map(r => {
            const videoId = getYouTubeId(r.youtube_url);
            return (
              <div key={r.id} style={s.card}>
                <div style={s.videoWrap}>
                  {videoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={r.title}
                      style={s.iframe}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div style={s.noThumb}><FiYoutube size={32} opacity={0.3} /></div>
                  )}
                </div>
                <div style={s.cardInfo}>
                  <div style={s.cardActions}>
                    <button style={{ ...s.editBtn, ...s.delBtn }} onClick={() => openDel(r)}>
                      <FiTrash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="md">
        <ModalHeader toggle={() => setModal(false)}>
          <FiYoutube style={{ marginRight: 8, color: '#ff0000' }} />Add YouTube Video
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="yt-url" style={{ fontWeight: 600 }}>
              YouTube URL <span style={{ color: '#ef4444' }}>*</span>
            </Label>
            <div style={{ position: 'relative' }}>
              <span style={s.urlIcon}><FiLink size={15} /></span>
              <Input
                id="yt-url"
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={form.youtube_url}
                onChange={e => { setForm({ youtube_url: e.target.value }); setError(''); }}
                style={{ paddingLeft: 36 }}
                invalid={!!error}
              />
            </div>
            {error && <div style={s.errText}>{error}</div>}
          </FormGroup>

          {previewId && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 12, opacity: 0.5, marginBottom: 8 }}>Preview</p>
              <img
                src={`https://img.youtube.com/vi/${previewId}/hqdefault.jpg`}
                alt="preview"
                style={{ width: '100%', borderRadius: 10, maxHeight: 180, objectFit: 'cover' }}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!form.youtube_url.trim() || saving}
            style={{ background: 'linear-gradient(135deg,#7c3aed,#ec4899)', border: 'none' }}
          >
            {saving ? 'Saving…' : 'Add Video'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal(false)}>
          <FiTrash2 style={{ marginRight: 8, color: '#ef4444' }} />Delete Video
        </ModalHeader>
        <ModalBody>
          <p style={{ margin: 0 }}>
            Remove this YouTube video from the portfolio? This will not delete the actual video.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button color="danger" onClick={handleDelete}>Remove</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const s = {
  wrap: { padding: '8px 0' },
  hdr:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center' },
  sub:   { margin: '4px 0 0', opacity: 0.5, fontSize: 13 },
  addBtn: {
    background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
    color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
    display: 'flex', alignItems: 'center', gap: 6,
    boxShadow: '0 4px 16px rgba(124,58,237,0.35)',
  },
  successBanner: {
    background: '#10b981',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  errorBanner: {
    background: '#ef4444',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20,
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14, overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  videoWrap: { position: 'relative', aspectRatio: '16/9', background: '#000' },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  noThumb: {
    width: '100%', height: '100%',
    background: 'rgba(255,255,255,0.03)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  cardInfo: { padding: '14px 16px' },
  cardActions: { display: 'flex', gap: 8 },
  editBtn: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', color: 'inherit', fontSize: 13,
    display: 'flex', alignItems: 'center', gap: 5,
  },
  delBtn: { background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.2)', color: '#ef4444' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: {
    width: 36, height: 36,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#7c3aed',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  empty: { textAlign: 'center', padding: '60px 20px', opacity: 0.5 },
  urlIcon: {
    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
    opacity: 0.5, pointerEvents: 'none',
  },
  errText: { color: '#ef4444', fontSize: 12, marginTop: 4 },
};

export default YoutubeManager;
